import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import isSelfService from '@/common/utils/isSelfService';
import { APP_NAME_HCI, APP_NAME_SELF_SERVICE, MAX_INACTIVE_TIME_MINUTES } from '@/common/constants/ui.constants';
import usePermissionsStore from '@/common/store/pinia/permissions';
import useAuthStore from '@/common/store/pinia/auth';
import polling from '@/common/utils/polling';
import { NotificationType } from '@/common/constants/common.constants';
import i18n from '@/common/i18n';
import AuthorizationError from '@/common/utils/errors/AuthorizationError';
import { store } from '@/common/root';
import jsonParseSafe from '@/common/utils/jsonParseSafe';
import useS3ServiceStore from '@/common/store/pinia/s3Service';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';
import { APP_THEME_MAP } from '@/common/constants/themes.constants';

export const useAppStore = defineStore('app', () => {
  const isFullscreen = ref(false);
  const detailsIsOpen = ref(false);

  const appName = computed(() => isSelfService() ? APP_NAME_SELF_SERVICE : APP_NAME_HCI);
  const isCustomBuild = computed(() => APP_THEME !== APP_THEME_MAP.virtuozzo);

  const fetchHciLandingPage = () => getClusterId()
      .then(() => 'monitoring')
      .catch(() => 'infrastructure');

  const fetchSsLandingPage = async () => {
    const authStore = useAuthStore();
    const s3ServiceStore = useS3ServiceStore();
    if (authStore.domainMode) {
      return 'DomainUsers';
    }
    await Promise.allSettled([
      store.dispatch('compute/FETCH_COMPUTE_CLUSTER'),
      s3ServiceStore.getS3Service()
    ]);
    if (store.getters['compute/isComputeConfigured']) {
      return 'monitoring';
    }
    if (s3ServiceStore.isS3Available) {
      return 's3';
    }
    return 'noService';
  };

  const aboutToStop = () => {
    // about_to_stop event is received on the Primary node reboot/shutdown.
    //  Unfortunately, the event is sent before the actual reboot start of the reboot during the upgrade,
    // that is why it is required to track when the backend is really stopped.
    // 1. start polling the endpoint until receive the first exception
    // 2. block the interaction to the page, display the notification
    // 3. run another polling and wait until the node is alive
    // 4. reload the application
    const permissionsStore = usePermissionsStore();
    const authStore = useAuthStore();

    const waitForReboot = new Promise<void>((resolve) => {
      const waitForRebootPolling = polling(() => authStore.getProfileInfo()
        .catch(() => {
          waitForRebootPolling.stop();
          resolve();
        })
      );
    });

    const waitForAlive = () => new Promise<void>((resolve) => {
      store.dispatch('notifications/ADD', {
        type: NotificationType.INFO,
        showClose: false,
        duration: 0,
        skipStore: true,
        title: i18n.t('Admin panel is temporarily unavailable'),
        message: i18n.t('Rebooting or shutting down the primary management node... Once the node is up again, you will be redirected to the login screen.')
      });

      // eslint-disable-next-line no-underscore-dangle
      (store as any)._vm.$loading({
        target: '#app',
        background: 'fixed-white',
        opacity: 0.8,
        size: '48',
        lock: true
      });

      polling(() => permissionsStore.fetchAuthMeta()
        .then(() => resolve())
        .catch((err) => {
          const leaveCounterKey = `leaveTime-${appName.value}`;
          const leaveCounterLocalStorage = window.localStorage.getItem(leaveCounterKey);
          const leaveCounterObject: { value?: number } | undefined = leaveCounterLocalStorage ? jsonParseSafe(leaveCounterLocalStorage) : {};
          const leaveTime = leaveCounterObject?.value || 0;
          if (err instanceof AuthorizationError || leaveTime >= MAX_INACTIVE_TIME_MINUTES * 60) {
            resolve();
          }
        }));
    });

    return waitForReboot
      .then(waitForAlive)
      .then(() => {
        window.location.reload();
      });
  };

  return {
    isFullscreen,
    detailsIsOpen,
    appName,
    isCustomBuild,
    fetchHciLandingPage,
    fetchSsLandingPage,
    aboutToStop
  };
}, { ws: { about_to_stop: 'aboutToStop' } });

export default useAppStore;
